import { useEffect, useRef } from 'react';
import { useLocation, useSearchParams } from 'react-router';

import { useInviteLeaseDetailsDrawerStore } from '@/stores/use-invite-lease-details-drawer.store';
import { RENTER_INVITE_QUERY_PARAM } from '@/constants/Constants';
import type { InviteEntryPoint, InviteNavigationState } from '../types';

type UseOpenInviteLeaseDetailsDrawerFromQueryParamProps = {
  enabled: boolean;
};

// Opens the drawer when the URL contains ?renter-invite=true (direct links, cross-page navigation).
export const useOpenInviteLeaseDetailsDrawerFromQueryParam = ({
  enabled,
}: UseOpenInviteLeaseDetailsDrawerFromQueryParamProps) => {
  const [searchParams] = useSearchParams();
  const location = useLocation();
  const { isOpen, openDrawer } = useInviteLeaseDetailsDrawerStore();

  // Consume each query-param signal once until it is cleared from the URL.
  const hasProcessedRef = useRef(false);

  useEffect(() => {
    if (!enabled) return;

    const shouldOpenFromQueryParam = searchParams.get(RENTER_INVITE_QUERY_PARAM) === 'true';

    // If drawer is open while the invite query param is present, mark it as processed.
    // This prevents immediate re-open loops during close while query-param cleanup runs.
    if (isOpen) {
      if (shouldOpenFromQueryParam) {
        hasProcessedRef.current = true;
      }
      return;
    }

    if (!shouldOpenFromQueryParam) {
      hasProcessedRef.current = false;
      return;
    }

    if (hasProcessedRef.current) return;

    hasProcessedRef.current = true;

    const navigationState = location.state as InviteNavigationState | null;
    const entryPoint: InviteEntryPoint = navigationState?.inviteEntryPoint ?? 'direct_link';
    openDrawer(entryPoint, navigationState?.fromUrl);
  }, [enabled, isOpen, searchParams, openDrawer, location.state]);
};
